import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://hldacglrddqchkjavfme.supabase.co'
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhsZGFjZ2xyZGRxY2hramF2Zm1lIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4Njc5NjIsImV4cCI6MjA4ODQ0Mzk2Mn0.Qs9eAHTqgECJzuVwV-yKrPDqmBNw-i1eqq14lNJzMi4'

const isConfigured = SUPABASE_URL.length > 10 && SUPABASE_ANON_KEY.length > 10

// Debug: log config status (remove after fixing)
console.log('Supabase URL:', SUPABASE_URL ? SUPABASE_URL.substring(0, 30) + '...' : 'MISSING')
console.log('Supabase Key:', SUPABASE_ANON_KEY ? SUPABASE_ANON_KEY.substring(0, 20) + '...' : 'MISSING')
console.log('isConfigured:', isConfigured)

// Safe mock client used when env vars are missing
const mockClient = {
  auth: {
    getSession: async () => ({ data: { session: null } }),
    onAuthStateChange: (cb) => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signUp: async () => ({ data: null, error: { message: 'Database not configured yet' } }),
    signInWithPassword: async () => ({ data: null, error: { message: 'Database not configured yet' } }),
    signOut: async () => ({ error: null }),
  },
  from: () => ({
    select: () => ({ eq: () => ({ single: async () => ({ data: null, error: null }), order: () => ({ limit: async () => ({ data: [], error: null }) }) }), order: () => ({ ascending: () => ({ limit: async () => ({ data: [], error: null }) }), limit: async () => ({ data: [], error: null }) }) }),
    insert: async () => ({ error: null }),
    update: () => ({ eq: async () => ({ error: null }) }),
    upsert: async () => ({ data: null, error: null }),
  })
}

export const supabase = isConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true }
    })
  : mockClient

// ── Auth helpers ────────────────────────────────────────────
export const signUp = async (email, password, name) => {
  if (!isConfigured) return { data: null, error: { message: 'App not fully configured. Contact support.' } }
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { full_name: name } }
  })
  return { data, error }
}

export const signIn = async (email, password) => {
  if (!isConfigured) return { data: null, error: { message: 'App not fully configured. Contact support.' } }
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

export const onAuthChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback)
}

// ── Profile helpers ─────────────────────────────────────────
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles').select('*').eq('id', userId).single()
  return { data, error }
}

export const upsertProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles').upsert({ id: userId, ...updates, updated_at: new Date().toISOString() })
  return { data, error }
}

// ── Picks helpers ───────────────────────────────────────────
export const getPicks = async () => {
  const { data, error } = await supabase
    .from('picks').select('*').eq('active', true)
    .order('created_at', { ascending: false })
  return { data, error }
}

export const trackPick = async (userId, pickId) => {
  const { data, error } = await supabase
    .from('tracked_picks').upsert({ user_id: userId, pick_id: pickId }, { onConflict: 'user_id,pick_id' })
  return { data, error }
}

export const untrackPick = async (userId, pickId) => {
  const { data, error } = await supabase
    .from('tracked_picks').delete()
    .eq('user_id', userId).eq('pick_id', pickId)
  return { data, error }
}

export const getTrackedPicks = async (userId) => {
  const { data, error } = await supabase
    .from('tracked_picks').select('*, picks(*)').eq('user_id', userId)
    .order('created_at', { ascending: false })
  return { data, error }
}

export const getUserStats = async (userId) => {
  const { data, error } = await supabase
    .from('user_stats').select('*').eq('user_id', userId).single()
  return { data, error }
}

export const getLeaderboard = async () => {
  const { data, error } = await supabase
    .from('leaderboard_view').select('*')
    .order('accuracy', { ascending: false }).limit(20)
  return { data, error }
}

export const getSubscription = async (userId) => {
  const { data, error } = await supabase
    .from('subscriptions').select('*').eq('user_id', userId)
    .eq('status', 'active').single()
  return { data, error }
}
